import React, { useRef, useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./RangeDatePicker.css";
import { useDispatch, useSelector } from "react-redux";
import {
  setStartDate,
  setEndDate,
} from "../../../redux/slices/operationHomeSlice";

const CalendarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="14"
    height="14"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const parseMMDDYYYY = (str) => {
  if (!str) return null;
  const [month, day, year] = str.split("/");
  if (!month || !day || !year) return null;
  const d = new Date(+year, +month - 1, +day);
  return isNaN(d.getTime()) ? null : d;
};

const formatMMDDYYYY = (date) => {
  if (!date) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const SHARED_PICKER_PROPS = {
  dateFormat: "MM/dd/yyyy",
  placeholderText: "MM/DD/YYYY",
  className: "date-text-input",
  wrapperClassName: "rdp-wrapper",
  showMonthDropdown: true,
  showYearDropdown: true,
  dropdownMode: "select",
  popperPlacement: "bottom-start",
  popperProps: { strategy: "fixed" },
};

const RangeDatePicker = ({ isSelected = false, onSendData, onClear, maxDate }) => {
  const dispatch = useDispatch();
  const startPickerRef = useRef(null);
  const endPickerRef = useRef(null);

  const committedStart = useSelector(
    (state) => state.operationHome.tabStartDate,
  );
  const committedEnd = useSelector((state) => state.operationHome.tabEndDate);

  const [localStart, setLocalStart] = useState(committedStart || "");
  const [localEnd, setLocalEnd] = useState(committedEnd || "");

  useEffect(() => {
    setLocalStart(committedStart || "");
    setLocalEnd(committedEnd || "");
  }, [committedStart, committedEnd]);

  if (!isSelected) return null;

  const startDate = parseMMDDYYYY(localStart);
  const endDate = parseMMDDYYYY(localEnd);
  const maxDateObj = maxDate instanceof Date ? maxDate : new Date();

  return (
    <div className="RDP-range-date-picker" onClick={(e) => e.stopPropagation()}>
      <div className="search-input-wrapper date-picker-wrapper">
        <span className="RDP-floating-label">Start Date</span>
        <DatePicker
          {...SHARED_PICKER_PROPS}
          ref={startPickerRef}
          selected={startDate}
          onChange={(date) => setLocalStart(formatMMDDYYYY(date))}
          maxDate={endDate || maxDateObj}
        />
        <button
          className="calendar-icon-btn"
          type="button"
          onClick={() => startPickerRef.current?.setOpen(true)}
        >
          <CalendarIcon />
        </button>
      </div>

      <span className="RDP-separator">–</span>

      <div className="search-input-wrapper date-picker-wrapper">
        <span className="RDP-floating-label">End Date</span>
        <DatePicker
          {...SHARED_PICKER_PROPS}
          ref={endPickerRef}
          selected={endDate}
          onChange={(date) => setLocalEnd(formatMMDDYYYY(date))}
          minDate={startDate || undefined}
          maxDate={maxDateObj}
        />
        <button
          className="calendar-icon-btn"
          type="button"
          onClick={() => endPickerRef.current?.setOpen(true)}
        >
          <CalendarIcon />
        </button>
      </div>

      <button
        className="RDP-ok-btn"
        type="button"
        disabled={!(localStart && localEnd)}
        onClick={() => {
          dispatch(setStartDate(localStart));
          dispatch(setEndDate(localEnd));
          onSendData({ startDate: localStart, endDate: localEnd });
        }}
      >
        OK
      </button>

      <button
        className="RDP-clear-btn"
        type="button"
        onClick={() => {
          setLocalStart("");
          setLocalEnd("");
          dispatch(setStartDate(null));
          dispatch(setEndDate(null));
          if (onClear) onClear();
        }}
      >
        ×
      </button>
    </div>
  );
};

export default RangeDatePicker;
